import { useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useHistory, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ContainerWrapper from "../../components/ContainerWrapper/ContainerWrapper";
import ActionButton from "../../components/Button/ActionButton";
import Loading from "../../components/Loading/Loading";
import HelpButton from "../../components/payments/HelpContent/HelpButton";
import ConfigurationSection from "../../components/payments/ConfigurationSection/ConfigurationSection";
import LabelledValue from "../../components/payments/ConfigurationSection/LabelledValue";
import AtCredentialsDialog from "../../components/payments/AtCredentialsDialog/AtCredentialsDialog";
import usePaymentConfigurations from "../../hooks/usePaymentConfigurations";
import { EasypayState } from "../../types/payments";
import { PAYMENTS_HELP_CONTENT } from "../../constants";
import { semanticColors } from "../../utils/colors";
import {
  CONFIGURATIONS_PATH,
  configurationDocumentsPath
} from "../../utils/paymentsRoutes";
import { typography } from "../../utils/typography";
import {
  centeredActionsSx,
  pageColumnSx,
  pageHeaderRowSx,
  pageTitleSx
} from "../../utils/pageStyles";

const notFoundColumnSx = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 2
};

const notFoundTextSx = {
  ...typography.body.l,
  color: semanticColors.text.primary
};

/**
 * States that all mean the same thing to the professional: Easypay has not
 * received the documents yet. They are collapsed into a single message so the
 * internal steps of the KYC pipeline do not leak into the UI.
 */
const AWAITING_DOCUMENTS_STATES: EasypayState[] = [
  "NOT_REGISTERED",
  "WAITING_IDS",
  "WAITING_DOCUMENTS_UPLOAD"
];

/**
 * Hub of a payment configuration: the three requirements that must all be
 * satisfied before it can be used to charge a patient.
 *
 * The configuration is derived from the list rather than fetched on its own —
 * the API exposes no endpoint for a single configuration.
 */
export default function PaymentConfigurationDetail() {
  const { t } = useTranslation();
  const history = useHistory();
  const { configId } = useParams<{ configId: string }>();
  const { configurations, loading, refetch } = usePaymentConfigurations();
  const [editingCredentials, setEditingCredentials] = useState(false);

  const configuration = useMemo(
    () => configurations.find(item => item.id === configId),
    [configurations, configId]
  );

  // Only while there is nothing to show: the focus refetch would otherwise
  // replace the whole page with a spinner, closing the credentials dialog.
  if (loading && !configuration) {
    return (
      <ContainerWrapper>
        <Box display="flex" justifyContent="center" alignItems="center" mt={10}>
          <Loading />
        </Box>
      </ContainerWrapper>
    );
  }

  if (!configuration) {
    return (
      <ContainerWrapper>
        <Box sx={notFoundColumnSx}>
          <Typography sx={notFoundTextSx}>
            {t("paymentConfigurationDetail.notFound")}
          </Typography>
          <ActionButton
            label={t("paymentConfigurationDetail.backToList")}
            onClick={() => history.push(CONFIGURATIONS_PATH)}
            variant="secondary"
          />
        </Box>
      </ContainerWrapper>
    );
  }

  const hasAtCredentials = Boolean(configuration.AT?.username);
  const documentsDone = configuration.easypayState === "DONE";
  // Collection closes the moment the professional confirms the submission, not
  // when Easypay comes back — from there on the documents screen either stands
  // read-only or steps aside for the review notice, so the action leading to it
  // must not keep inviting an upload.
  //
  // `documentsReady` is checked as well as the state because the two move
  // separately: the flag is the professional's confirmation, the state is moved
  // by the backend afterwards, and a configuration sits between the two.
  const documentsClosed =
    documentsDone ||
    configuration.easypayState === "REVIEWING" ||
    Boolean(configuration.documentsReady);
  const easypayStateLabel = AWAITING_DOCUMENTS_STATES.includes(
    configuration.easypayState
  )
    ? t("paymentConfigurationDetail.awaitingDocuments")
    : t(`easypayState.${configuration.easypayState}`);

  const header = (
    <Box sx={pageHeaderRowSx}>
      <Typography sx={pageTitleSx}>
        {t("paymentConfigurationDetail.title")}
      </Typography>
      <HelpButton linkName={PAYMENTS_HELP_CONTENT.UPDATE_CONFIG} />
    </Box>
  );

  const accountSection = (
    <ConfigurationSection
      title={t("paymentConfigurationDetail.accountSection")}
    >
      <LabelledValue
        label={t("paymentConfigurationDetail.billingName")}
        value={configuration.organizationName}
      />
      <LabelledValue
        label={t("paymentConfigurationDetail.fiscalId")}
        value={configuration.fiscalId}
      />
    </ConfigurationSection>
  );

  const atSection = (
    <ConfigurationSection
      title={t("paymentConfigurationDetail.atSection")}
      valid={hasAtCredentials}
    >
      <LabelledValue
        label={t("paymentConfigurationDetail.stateLabel")}
        value={
          hasAtCredentials
            ? t("paymentConfigurationDetail.atValid")
            : t("paymentConfigurationDetail.atInvalid")
        }
      />
      <Box>
        <ActionButton
          label={
            hasAtCredentials
              ? t("paymentConfigurationDetail.updateDetails")
              : t("paymentConfigurationDetail.registerDetails")
          }
          onClick={() => setEditingCredentials(true)}
          variant={hasAtCredentials ? "secondary" : "primary"}
        />
      </Box>
    </ConfigurationSection>
  );

  const documentsSection = (
    <ConfigurationSection
      title={t("paymentConfigurationDetail.documentsSection")}
      valid={documentsDone}
    >
      <LabelledValue
        label={t("paymentConfigurationDetail.companyTypeLabel")}
        value={t(`companyType.${configuration.easypayCompanyType}`)}
      />
      <LabelledValue
        label={t("paymentConfigurationDetail.stateLabel")}
        value={easypayStateLabel}
      />
      <Box>
        <ActionButton
          label={
            documentsClosed
              ? t("paymentConfigurationDetail.consultDocuments")
              : t("paymentConfigurationDetail.uploadDocuments")
          }
          onClick={() => history.push(configurationDocumentsPath(configId))}
          variant={documentsClosed ? "secondary" : "primary"}
        />
      </Box>
    </ConfigurationSection>
  );

  const backToList = (
    <Box sx={centeredActionsSx}>
      <ActionButton
        label={t("paymentConfigurationDetail.backToList")}
        onClick={() => history.push(CONFIGURATIONS_PATH)}
        variant="secondary"
      />
    </Box>
  );

  return (
    <ContainerWrapper>
      <Box sx={pageColumnSx}>
        {header}
        {accountSection}
        {atSection}
        {documentsSection}
        {backToList}
      </Box>

      <AtCredentialsDialog
        open={editingCredentials}
        configuration={configuration}
        onClose={() => setEditingCredentials(false)}
        onSaved={refetch}
      />
    </ContainerWrapper>
  );
}
